import { MinusCircleFilled, CaretDownOutlined } from '@ant-design/icons';
import { Flex } from '@ui-library/misc';
import Modal from '@ui-library/modal/custom';
import { LabelMedium } from '@ui-library/typography';
import DestinationIcon from '@components/common/icons/destinationIcon';
import SourceIcon from '@components/common/icons/sourceIcon';
import { Header, SubHeader } from '@components/sources/source/warehouseSource/configureData/style';
import { fetchErrorAssets } from '@components/sources/visualizer/mappingScreens/errorsView';
import theme from '@css/theme';
import useModalView from '@hooks/useModalView';
import { Checkbox, Skeleton, Select } from 'antd';
import Tooltip from '@ui-library/tooltip';
import * as React from 'react';
import styled from 'styled-components';
import { FixMe } from '@utils/types';
import { IColumn } from '@components/sources/source/warehouseSource/configureData/sourceSpecificConfig';
import { IField, IMappingConfig, useConnectionState } from '../../../visualDataMappingProvider';
import classes from '../mapping-commons.module.scss';
import SelectFieldMapping from '../SelectFieldMapping';
import {
  RefreshButtonTextProps,
  refreshColumnsSetup,
  refreshFieldsSetup,
  reloadButton,
} from '../SelectUniqueIdentifier';
import { FieldView, PreviewView, TableSelect, ColumnView } from './mappingViewHelpers';
import MoreActionsDropdown from './moreActionsDropdown';
import ErrorLabel from '@ui-library/errorLabel';

const CheckboxCustom = styled(Checkbox)`
  margin-right: 10px !important;
  .ant-checkbox-checked {
    .ant-checkbox-inner {
      background-color: #0085ff;
      border-color: #0085ff;
      color: white;
      border-radius: 2px;

      &::after {
        border-color: white;
      }
    }
  }
`;

const { Option } = Select;
const setUnidentified = (name?: string) => {
  const unIdentified = {
    type: '',
    name,
    label: name,
    unidentified: true,
  };
  return unIdentified;
};

export const getFieldDetails = (fieldMap: { [name: string]: IField }, name?: string) =>
  fieldMap[name || ''] || setUnidentified(name);
export const getColumnDetails = (columnMap: { [name: string]: IColumn }, name?: string) =>
  columnMap[name || ''] || setUnidentified(name);
const MappingInfo = () => {
  const {
    fields,
    mappingConfig,
    preview,
    setMappingConfig,
    uniqueIdentifierConfig,
    fieldMap,
    columnMap,
    source,
    setFields,
    destination,
    columns,
    viewOnly,
    enteredSaveState,
    setBottomEditButtonVisible,
    updateSchemaErrors,
  } = useConnectionState();
  const [mappingDataSource, setMappingDataSource] = React.useState<IMappingConfig[]>([
    uniqueIdentifierConfig,
    ...(mappingConfig || []),
    {},
  ]);
  const sourceDefName = source.sourceDef?.name || '';

  const [showUnmappedCheck, setShowUnmappedCheck] = React.useState(false);
  const [resetMappingsModal, showResetMappingsModal] = useModalView(
    <Modal
      title={<div>Hold up!</div>}
      content={
        <LabelMedium secondary>Resetting will remove all the fields in the mappings</LabelMedium>
      }
      onClose={() => showResetMappingsModal(false)}
      saveLabel="Yes, reset"
      saveLabelBtnType="secondary"
      onSave={() => {
        const updateFields = fields.filter((curr) => !curr.createdNow);
        setFields(updateFields, true);
        showResetMappingsModal(false);
      }}
    />,
  );
  const updateOriginal = (map: IMappingConfig) => {
    const updatedConfig: IMappingConfig[] = mappingConfig.map((curr: IMappingConfig) =>
      map?.mapped === curr?.mapped ? { mapped: map.mapped, original: map.original } : curr,
    );

    setMappingConfig(updatedConfig, () => {
      if (getFieldDetails(fieldMap, map.mapped)?.createdNow) {
        const newFields = fields.map((field: IField) =>
          field.name === map.mapped
            ? { ...field, type: columnMap[map.original || '']?.type }
            : field,
        );
        setFields(newFields);
      }
    });
  };
  React.useEffect(() => {
    if (showUnmappedCheck) {
      const updatemappingDataSource = mappingConfig.filter((mapping) => !mapping.original);
      setMappingDataSource([uniqueIdentifierConfig, ...updatemappingDataSource, {}]);
    } else {
      setMappingDataSource([uniqueIdentifierConfig, ...(mappingConfig || []), {}]);
    }
  }, [mappingConfig, showUnmappedCheck]);

  // eslint-disable-next-line react/no-unstable-nested-components
  const ColumnSelectionView = ({
    column,
    field,
    showAsText,
  }: {
    column: IColumn;
    field: IField;
    showAsText?: boolean;
  }) => {
    const columnItem: FixMe = getColumnDetails(columnMap, column.name);
    const hasError = !!column.name && showAsText && columnItem?.unidentified;

    const onSelectingColumn = (c: FixMe) => {
      if (field?.name) {
        if (showUnmappedCheck) {
          setTimeout(() => {
            updateOriginal({ original: c as string, mapped: field?.name });
          }, 500);
        } else {
          updateOriginal({ original: c as string, mapped: field?.name });
        }
      }
    };
    return (
      <div className={`${classes.column_selector_container} ${hasError ? classes.has_error : ''}`}>
        {showAsText ? (
          <FieldView columnsSide field={columnItem} />
        ) : (
          <TableSelect
            onClick={(e: FixMe) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            suffixIcon={<CaretDownOutlined />}
            hasError={(column?.name || enteredSaveState) && !columnMap[column?.name]}
            value={column?.name}
            showSearch
            placeholder="Select or add column"
            onChange={onSelectingColumn}
          >
            {columns.map((col) => (
              <Option value={col.name} key={`single-select-opt-key-${col.name}-${col.name}`}>
                <ColumnView column={getColumnDetails(columnMap, col.name)} />
              </Option>
            ))}
          </TableSelect>
        )}
      </div>
    );
  };
  const mappingListItem = (listItem: IMappingConfig, index: number) => {
    const field = getFieldDetails(fieldMap, listItem.mapped);

    return (
      <Flex className={classes.list_item}>
        <Flex className={classes.left_list_item}>
          {!!index &&
            index !== mappingDataSource.length - 1 &&
            !viewOnly &&
            !field.requiredForMapping && (
              <Tooltip title="Remove">
                <MinusCircleFilled
                  className={classes.remove_mapping_icon}
                  onClick={() => {
                    if (field?.createdNow) {
                      const updateFields = fields.filter((curr) => curr.name !== field.name);
                      setFields(updateFields);
                    }
                    setMappingConfig(mappingConfig.filter((curr) => curr.mapped !== field.name));
                  }}
                />
              </Tooltip>
            )}
        </Flex>
        <Flex className={classes.right_list_item}>
          {index !== mappingDataSource.length - 1 && (
            <ColumnSelectionView
              field={getFieldDetails(fieldMap, listItem.mapped)}
              column={getColumnDetails(columnMap, listItem.original)}
              showAsText={!index || viewOnly}
            />
          )}

          {index !== mappingDataSource.length - 1 && <PreviewView column={listItem.original} />}

          {index !== mappingDataSource.length - 1 && (
            <FieldView field={getFieldDetails(fieldMap, listItem.mapped)} />
          )}

          {index === mappingDataSource.length - 1 && (
            <div className={classes.add_field_button_wrapper}>
              <SelectFieldMapping
                onChangeMapping={onChangeMapping}
                onSelect={(fieldSelected) => {
                  onChangeMapping({ mapped: fieldSelected.name });
                }}
              />
            </div>
          )}
        </Flex>
      </Flex>
    );
  };
  const showUnmappedOnly = (e: FixMe) => {
    const { checked } = e.target;
    if (checked) {
      setShowUnmappedCheck(true);
    } else {
      setShowUnmappedCheck(false);
    }
  };
  const onChangeMapping = (mapping: { original?: string; mapped?: string }, cl?: FixMe) => {
    if (setMappingConfig) {
      setMappingConfig(
        Object.prototype.hasOwnProperty.call(mapping, 'original')
          ? mappingConfig.map((map: IMappingConfig, i: number) => {
              if (map.mapped === mapping.mapped && !i) {
                return mapping;
              }
              return map;
            })
          : [...mappingConfig, mapping],
        cl,
      );
    }
  };
  const reloadButtonComp = (props: RefreshButtonTextProps, tooltipText: string) => (
    <div className={classes.mapping_view_reload_wrapper}>
      <Tooltip title={tooltipText}>{reloadButton(props)}</Tooltip>
    </div>
  );

  const mappingsListBody = () => {
    if (setBottomEditButtonVisible) {
      setBottomEditButtonVisible(true);
    }
    return Object.keys(preview).length > 0 ? (
      [...mappingDataSource.map((listitem, index) => mappingListItem(listitem, index))]
    ) : (
      <Skeleton active paragraph={{ rows: 4 }} />
    );
  };

  const mappingsListHeader = () => {
    const fieldRefreshCompGenerator = (props: RefreshButtonTextProps) => {
      const tooltipText = 'Reload fields';
      return reloadButtonComp(props, tooltipText);
    };

    const colRefreshCompGenerator = (props: RefreshButtonTextProps) => {
      const tooltipText = 'Reload columns';
      return reloadButtonComp(props, tooltipText);
    };
    return (
      <Flex className={classes.list_item}>
        <Flex className={classes.left_list_item} />
        <Flex className={classes.right_list_item}>
          <div className={classes.vdmTable_header}>
            <SourceIcon source={sourceDefName} size={theme.iconSize.medium} />
            <div className={classes.header}>Warehouse columns</div>
            {refreshColumnsSetup(!!viewOnly, colRefreshCompGenerator)}
          </div>
          <div className={classes.vdmTable_preview_header}>PREVIEW</div>
          <div className={classes.vdmTable_header}>
            <DestinationIcon destination={destination?.destinationDefinition?.name} />
            <div className={classes.header}>Destination fields</div>
            {refreshFieldsSetup(
              Boolean(viewOnly),
              fieldRefreshCompGenerator,
              (val: boolean) => val && updateSchemaErrors([fetchErrorAssets('fields')]),
            )}
          </div>
        </Flex>
      </Flex>
    );
  };

  const requiredFieldsLength = fields?.filter((field: IField) => !!field.requiredForMapping).length;

  const removedColumnsCount = mappingConfig.filter(
    ({ original }) => original && !columnMap[original],
  ).length;
  const removedFieldsCount = mappingConfig.filter(
    ({ mapped }) => mapped && !fieldMap[mapped],
  ).length;

  return (
    <div className={classes.mapping_view_wrapper}>
      {resetMappingsModal}
      <div className={classes.mapping_view_header}>
        <div className={classes.mapping_view_header_left}>
          <Header>Map fields</Header>
          <SubHeader>
            Configure which column from your source maps to which destination field.
          </SubHeader>

          <SubHeader>
            {requiredFieldsLength > 0 &&
              !viewOnly &&
              `This destination requires the following
               ${requiredFieldsLength.toLocaleString()} 
              field
              ${requiredFieldsLength > 1 ? 's' : ''}
              :`}
          </SubHeader>
          <ErrorLabel
            error={Boolean(removedColumnsCount)}
            errorMessage={`${removedColumnsCount} warehouse column(s) not found. Remove or remap them.`}
          />
          <ErrorLabel
            error={Boolean(removedFieldsCount)}
            errorMessage={`${removedFieldsCount} destination field(s) not found. Remove them.`}
          />
        </div>
        {!viewOnly && (
          <div className={classes.mapping_view_header_right}>
            <CheckboxCustom onChange={showUnmappedOnly}>
              <span style={{ color: theme.color.rsPrimary }}>Show unmapped only</span>
            </CheckboxCustom>
            <MoreActionsDropdown showResetMappingsModal={showResetMappingsModal} />
          </div>
        )}
      </div>

      <div className={classes.mapping_view_body}>
        {mappingsListHeader()}
        {mappingsListBody()}
      </div>
    </div>
  );
};

export default MappingInfo;
